import { MRT_ColumnFiltersState, MRT_SortingState, MRT_Updater } from 'material-react-table';
import Papa from 'papaparse';
import { EQUAL_FILTERS, RANGE_FILTERS, RangeFilter } from './constants';
import { IconName } from '@/app/shared-components/Types/Icons';
import { Paged } from '@/app/shared-components/Types/Paged';
import { sanitizeMaskedInputValue } from './formatters';
import { addHours, getDate, getMonth, getYear, startOfDay } from 'date-fns';
import _ from '@lodash';
import { EventGroup, EventType } from 'app/shared-components/Types/Activity';

export const sanitize = (value: string | undefined) => {
  return value === '' ? undefined : value;
};

export const languageShortnames = {
  English: 'en',
  Spanish: 'es',
};

export const removeEmptyValues = (obj: any) => {
  Object.keys(obj).forEach((key) =>
    obj[key] && typeof obj[key] === 'object'
      ? removeEmptyValues(obj[key])
      : (obj[key] === '' || obj[key] == null) && delete obj[key],
  );
  return obj;
};

export const createFileDownloadLink = (
  res: { data: Blob },
  fileName: string,
  extension = 'csv',
) => {
  if (!window) {
    return;
  }
  const blobUrl = window.URL.createObjectURL(res.data);
  const anchor = window.document.createElement('a');
  anchor.download = `${fileName}-${new Date().toLocaleString()}.${extension}`;
  anchor.href = blobUrl;
  anchor.click();
  window.URL.revokeObjectURL(blobUrl);
};

/**
 * Export data to CSV and trigger download.
 * Uses PapaParse to convert data array to CSV format.
 *
 * @param data - Array of objects to export
 * @param fileName - Name of the downloaded file (without extension)
 * @param columns - Optional column configuration for custom headers and ordering
 */
export const exportToCsv = <T extends Record<string, unknown>>(
  data: T[],
  fileName: string,
  columns?: { key: keyof T; header: string }[],
) => {
  if (!window || !data || data.length === 0) {
    return;
  }

  let csvData: Record<string, unknown>[];

  if (columns) {
    // Map data to use custom headers and column ordering
    csvData = data.map((row) => {
      const mappedRow: Record<string, unknown> = {};
      columns.forEach(({ key, header }) => {
        mappedRow[header] = row[key];
      });
      return mappedRow;
    });
  } else {
    csvData = data;
  }

  const csv = Papa.unparse(csvData);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const blobUrl = window.URL.createObjectURL(blob);
  const anchor = window.document.createElement('a');
  anchor.download = `${fileName}-${new Date().toLocaleString().replace(/[/:]/g, '-')}.csv`;
  anchor.href = blobUrl;
  anchor.click();
  window.URL.revokeObjectURL(blobUrl);
};

export const uploadCSVFileFromBrowser = async () => {
  return _.has(window, 'showOpenFilePicker')
    ? uploadCSVFileFromBrowserWithAPI()
    : uploadCSVFileFromBrowserForNonSupportedBrowsers();
};

const uploadCSVFileFromBrowserWithAPI = async () => {
  try {
    const [fileHandle] = await window.showOpenFilePicker({
      types: [
        {
          description: 'CSV Files',
          accept: {
            'text/csv': ['.csv'],
          },
        },
      ],
      multiple: false,
    });

    return await fileHandle.getFile();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return undefined;
    }
    throw error;
  }
};

const uploadCSVFileFromBrowserForNonSupportedBrowsers = async () => {
  return new Promise<File | undefined>((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files?.length > 0) {
        resolve(target.files[0]);
      } else {
        resolve(undefined);
      }
    };
    input.onerror = (error) => {
      reject(error);
    };
    document.body.appendChild(input);
    input.click();
  });
};

export const parseCSVFile = async <T = Record<string, string>>(file: File) => {
  const csvText = await file.text();
  const parsed = Papa.parse<T>(csvText, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: false,
  });
  return parsed.data;
};

export const getS3UrlAndUpload = async ({
  file,
  getUploadUrlFunction,
}: {
  file: File;
  getUploadUrlFunction: (params: { fileName: string; contentType: string }) => Promise<any>;
}): Promise<string> => {
  const contentType = file.type || 'text/csv';
  const res = await getUploadUrlFunction({
    fileName: file.name,
    contentType,
  });

  const payload = res?.data ?? res;
  const { key, url } = payload ?? {};

  if (!key || !url) throw new Error('Invalid response from getUploadUrlFunction');

  // upload to S3
  await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });

  return key;
};

export const determineCompanyUrl = (companyId?: string, includeNullCompanyId?: boolean) => {
  if (companyId) {
    return `,referralCompanyId==${companyId}`;
  } else if (includeNullCompanyId) {
    return `,referralCompanyId==${null}`;
  } else {
    return '';
  }
};

export const createMyUtiliesURL = (filters: MRT_ColumnFiltersState, sorting: MRT_SortingState) => {
  const params = new URLSearchParams();

  if (filters?.length > 0) {
    const filterParams: string[] = [];
    filters.forEach((filter) => {
      if (EQUAL_FILTERS.includes(filter.id)) {
        filterParams.push(`${filter.id}==${filter.value}`);
      } else if (RANGE_FILTERS.includes(filter.id as RangeFilter)) {
        if (Array.isArray(filter.value)) {
          filterParams.push(`${filter.id}>=${filter.value[0]},${filter.id}<=${filter.value[1]}`);
        } else {
          filterParams.push(`${filter.id}>=${filter.value}`);
        }
      } else {
        if (filter.id === 'phone') {
          const phone = filter.value as string;
          filterParams.push(`${filter.id}==${sanitizeMaskedInputValue(phone, phone.length)}`);
        } else {
          filterParams.push(`${filter.id}@=*${filter.value}`);
        }
      }
    });
    params.append('filters', filterParams.join(','));
  }

  if (sorting?.length > 0) {
    let sortParams = '';
    sorting.forEach((sort) => {
      sortParams += sort.desc ? `-${sort.id}` : `${sort.id}`;
    });
    params.append('sorts', sortParams);
  }

  return params;
};

export const createFilterString = (filter: { id: string; value: any }) => {
  if (RANGE_FILTERS.includes(filter.id as RangeFilter)) {
    if (Array.isArray(filter.value)) {
      return `${filter.id}>=${filter.value[0]},${filter.id}<=${filter.value[1]}`;
    }
    return `${filter.id}>=${filter.value}`;
  }
  if (EQUAL_FILTERS.includes(filter.id)) {
    return `${filter.id}==${filter.value}`;
  }
  return `${filter.id}@=*${filter.value}`;
};

export const parseFilterString = (filterStr: string | null) => {
  if (!filterStr) {
    return [];
  }

  const filters: { [key: string]: string } = {};

  const parts = filterStr.split(',');
  for (const part of parts) {
    let operator: string;
    let operatorIndex: number;

    if (part.includes('>=') || part.includes('<=') || part.includes('==') || part.includes('@=*')) {
      const pattern = /(>=|<=|==|@=\*)/;
      const match = pattern.exec(part);
      operator = match ? match[0] : '';
      operatorIndex = part.indexOf(operator);
    } else {
      continue;
    }

    const id = part.substring(0, operatorIndex);
    const value = part.substring(operatorIndex + operator.length);

    if (filters[id] && (operator === '>=' || operator === '<=')) {
      // if the key already exists and its range filter, append the value with the operator and key
      filters[id] += ',' + id + operator + value;
    } else {
      // if the key does not exist or its not a range filter, set the value without the operator
      filters[id] = value;
    }
  }
  return Object.entries(filters).map(([id, value]) => ({
    id,
    value,
  }));
};

export const parseSortString = (sortStr: string | null) => {
  if (!sortStr) {
    return [];
  }
  const sortParts = sortStr.split(',');
  return sortParts.map((part) => {
    const desc = part.startsWith('-');
    const id = desc ? part.substring(1) : part;
    return { id, desc };
  });
};

export const hexToRgba = (hex: string, opacity: number) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return `rgba(${r},${g},${b},${opacity})`;
};

type StageColors = {
  [key: string]: string;
};

const colorsByStage: StageColors = {
  sales: 'rgb(223,186,201)',
  leads: 'rgb(138,151,183)',
  processing: 'rgb(255,206,190)',
  cancelled: 'rgb(255,123,123)',
  completed: 'rgb(165, 206, 57)',
};

const colorsByStageInDarkMode: StageColors = {
  sales: 'rgb(90,60,75)',
  leads: 'rgb(55,65,95)',
  processing: 'rgb(100,70,60)',
  cancelled: 'rgb(120,50,50)',
  completed: 'rgb(60,85,30)',
};

const salesPageColorsByStatus: StageColors = {
  '1': '#A2B255',
  '2': '#BBD6EB',
  '3': '#F67B5A',
};

const salesPageColorsByStatusInDarkMode: StageColors = {
  '1': '#4A5528',
  '2': '#3A5570',
  '3': '#8B4030',
};

export const getColorByStage = (stage: string, isDarkMode = false) => {
  const colors = isDarkMode ? colorsByStageInDarkMode : colorsByStage;
  return colors[stage] || null;
};

export const getSalesPageColorByStatus = (status: string, isDarkMode = false) => {
  const colors = isDarkMode ? salesPageColorsByStatusInDarkMode : salesPageColorsByStatus;
  return colors[status] || null;
};

const serviceToIconMapping: { [key: string]: IconName } = {
  Cleaning: 'house',
  'Homeowners Insurance': 'insurance',
  'Renters Insurance': 'insurance',
  'Washer & Dryer': 'apartment',
  Storage: 'vault',
  'Home Security': 'security',
  Electricity: 'electricity',
  Water: 'power',
  Security: 'security',
  Gas: 'fire',
  Internet: 'internet',
  Phone: 'phone',
  TV: 'tv',
  Bundle: 'sync',
};

export const convertServiceTypesToPopoverItems = (
  serviceTypes:
    | Paged<{
        id: number;
        name: string;
      }>
    | undefined,
  servicesHandleFilterChange: (newFiltersUpdater: MRT_Updater<MRT_ColumnFiltersState>) => void,
) => {
  if (!serviceTypes?.items || !serviceTypes.items.length) return [];

  return serviceTypes.items.map((service) => ({
    icon: serviceToIconMapping[service.name] || 'info',
    text: service.name,
    onClick: () => {
      servicesHandleFilterChange((prevFilters) => {
        const isTypeFilterPresent = prevFilters.some((filter) => filter.id === 'serviceTypeId');
        if (isTypeFilterPresent) {
          return prevFilters.map((filter) =>
            filter.id === 'serviceTypeId'
              ? { id: 'serviceTypeId', value: String(service.id) }
              : filter,
          );
        }

        return [...prevFilters, { id: 'serviceTypeId', value: String(service.id) }];
      });
    },
  }));
};

export const getCardType = (cardNumber: string) => {
  const patterns: { [key: string]: RegExp } = {
    visa: /^4[0-9]{0,15}$/,
    mastercard: /^(5[1-5][0-9]{0,14}|2[2-7][0-9]{0,14})$/,
    amex: /^3[47][0-9]{0,13}$/,
    discover: /^6(?:011|5[0-9]{2})[0-9]{0,12}$/,
  };

  for (const card in patterns) {
    if (patterns[card].test(cardNumber)) {
      return card;
    }
  }

  return '';
};

export const getMask = (cardType: string) => {
  switch (cardType) {
    case 'amex':
      return '9999-999999-99999';
    case 'visa':
    case 'mastercard':
    case 'discover':
      return '9999-9999-9999-9999';
    default:
      return '';
  }
};

export const createUTCWholeDay = (date: Date) => {
  const localNoonStart = addHours(startOfDay(new Date(date)), 12);
  const utcStartDay = new Date(
    Date.UTC(
      getYear(localNoonStart),
      getMonth(localNoonStart),
      getDate(localNoonStart),
      0,
      1,
      0,
      0,
    ),
  );
  const utcEndDay = new Date(
    Date.UTC(
      getYear(localNoonStart),
      getMonth(localNoonStart),
      getDate(localNoonStart),
      23,
      59,
      0,
      0,
    ),
  );

  return { utcStartDay, utcEndDay };
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export interface EnumToArrayOptions {
  nameFormatter?: (key: string) => string;

  /**
   *
   * @default 'all'
   */
  filterType?: 'all' | 'string' | 'numeric';
}

export function enumToArray<T extends Record<string, string | number>>(
  enumObj: T,
  options: EnumToArrayOptions = {},
): { id: string | number; name: string }[] {
  const {
    nameFormatter = (key: string) =>
      key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (char) => char.toUpperCase())
        .trim(),
    filterType = 'numeric',
  } = options;

  return Object.entries(enumObj)
    .filter(([, value]) => {
      if (filterType === 'string') {
        return isNaN(Number(value));
      }
      if (filterType === 'numeric') {
        return !isNaN(Number(value));
      }
      return true;
    })
    .map(([key, id]) => ({
      id,
      name: nameFormatter(key),
    }));
}

export const eventTypeGroups: Record<EventType, EventGroup> = {
  [EventType.CreationEvent]: 'system',
  [EventType.IncomingEmail]: 'system',
  [EventType.OutgoingEmail]: 'system',
  [EventType.IncomingMessage]: 'system',
  [EventType.OutgoingMessage]: 'system',
  [EventType.IncomingPhoneCall]: 'system',
  [EventType.OutgoingPhoneCall]: 'system',
  [EventType.Note]: 'note',
  [EventType.StatusChange]: 'statusChange',
  [EventType.StageChange]: 'statusChange',
  [EventType.Scheduled]: 'task',
  [EventType.FollowUp]: 'task',
  [EventType.Call]: 'task',
  [EventType.Text]: 'task',
  [EventType.Email]: 'task',
  [EventType.Reminder]: 'task',
  [EventType.Other]: 'note',
  [EventType.Snooze]: 'task',
  [EventType.VaultUpdated]: 'system',
  [EventType.VaultLinkSent]: 'system',
  [EventType.Edit]: 'system',
  [EventType.System]: 'system',
  [EventType.Dsi]: 'system',
};
export type OrderTimelineGroup =
  | 'creation'
  | 'email'
  | 'message'
  | 'call'
  | 'note'
  | 'status'
  | 'stage'
  | 'schedule'
  | 'edit'
  | 'other';

export function arraysEqualByKeys<T extends object, K extends keyof T>(
  a: T[],
  b: T[],
  keysToInclude: K[],
): boolean {
  if (a.length !== b.length) return false;

  // Omit ignored keys from both arrays
  const aProcessed = a.map((obj) => _.pick(obj, keysToInclude) as Pick<T, K>);
  const bProcessed = b.map((obj) => _.pick(obj, keysToInclude) as Pick<T, K>);

  const used = new Set<number>();

  return aProcessed.every((objA) => {
    const index = bProcessed.findIndex((objB, i) => !used.has(i) && _.isEqual(objA, objB));
    if (index === -1) return false;
    used.add(index);
    return true;
  });
}
